import { useCallback, useContext, useEffect, useRef } from 'react';
import { appActions, AppContext } from 'context';
import { OnboardingController } from 'networking/controllers/onboarding-controller';

const ORDER: OnboardingStatus[] = ['pending', 'intro_seen', 'completed'];
const MEMORY_ADDED = 'gs:memory-added';

// Call wherever a memory is saved (upload, note, recording, import). It ends
// the returning-customer onboarding: the New tooltip stops showing. The API
// does the same on its side when the memory is created.
export const publishMemoryAdded = () => {
  window.dispatchEvent(new Event(MEMORY_ADDED));
};

export const subscribeMemoryAdded = (listener: () => void) => {
  window.addEventListener(MEMORY_ADDED, listener);
  return () => window.removeEventListener(MEMORY_ADDED, listener);
};

// Moves the signed-in user's onboarding forward: right away in the app, then
// on the server. Never moves it back.
export const useAdvanceOnboarding = () => {
  const { state, dispatch } = useContext(AppContext);
  const userRef = useRef(state.data.user);
  userRef.current = state.data.user;

  return useCallback(
    async (status: OnboardingStatus) => {
      const user = userRef.current;
      const current = ORDER.indexOf(user.onboardingStatus ?? 'completed');
      if (user.id <= 0 || ORDER.indexOf(status) <= current) return;
      const updated = {
        ...user,
        onboardingStatus: status,
        firstVisitDisplayed: true,
      };
      userRef.current = updated;
      dispatch({ type: appActions.USER_LOGGED, data: { user: updated } });
      try {
        await OnboardingController.advance(status);
      } catch {
        // The API also completes it when the memory is created; a failed
        // update only means the introduction or tooltip may show once more.
      }
    },
    [dispatch],
  );
};

// While the New tooltip is up, any saved memory completes the onboarding.
export const useCompleteOnboardingOnMemoryAdded = (enabled: boolean) => {
  const advance = useAdvanceOnboarding();
  useEffect(() => {
    if (!enabled) return undefined;
    return subscribeMemoryAdded(() => {
      advance('completed');
    });
  }, [enabled, advance]);
};
